import axios from "axios";
import { App_Version, BASE_URL } from "../constants";
import { REFRESH_TOKEN } from "./configuration";
import { toast } from "react-toastify";
import { authentication } from "../components/common/authentication";
import Cookies from "js-cookie";

const config = {
  timeout: 10000,
};
let isRefreshing = false;
let isReLogin = false;
let isRefreshPage = false;
// When an RNP 408 fires, all concurrent failing calls queue on this promise.
// It resolves once the user successfully verifies their PIN, triggering auto-retry.
let rnpVerifyPromise = null;

const getAccessTokenSecondsLeft = () => {
  try {
    const expiry = Cookies.get("access_token_expiry");
    if (!expiry) return null;
    const exp = Number(expiry);
    if (!exp) return null;
    return exp - Math.floor(Date.now() / 1000);
  } catch {
    return null;
  }
};

// _skipProactive is an internal flag used on retry-after-failed-refresh to
// prevent re-entering the proactive refresh path indefinitely.
export const handleApi = (
  endpoint,
  data,
  cancelToken,
  navigate,
  header,
  options,
  prevRoute,
  _skipProactive = false,
  _skipRNPRetry = false,
) => {
  return new Promise((resolve, reject) => {
    // ── Proactive token refresh ───────────────────────────────────────────
    // If the access token expires within 60 s, refresh before making the call.
    // Call 1  → starts the refresh (isRefreshing = true).
    // Calls 2+ → queue via setInterval and retry once refresh completes.
    const secondsLeft = getAccessTokenSecondsLeft();
    const isExpiringSoon =
      !_skipProactive &&
      endpoint?.url !== REFRESH_TOKEN.url &&
      secondsLeft !== null &&
      secondsLeft <= 180;

    if (isExpiringSoon) {
      if (!isRefreshing) {
        isRefreshing = true;
        handleApi(REFRESH_TOKEN, null, null, navigate)
          .then(() => {
            isRefreshing = false;
            handleApi(
              endpoint,
              data,
              cancelToken,
              navigate,
              header,
              options,
              prevRoute,
            )
              .then(resolve)
              .catch(reject);
          })
          .catch(() => {
            isRefreshing = false;
            // Pass _skipProactive=true so the retry does not loop back here.
            handleApi(
              endpoint,
              data,
              cancelToken,
              navigate,
              header,
              options,
              prevRoute,
              true,
            )
              .then(resolve)
              .catch(reject);
          });
      } else {
        // Another call already started the refresh — wait for it, then retry.
        const interval = setInterval(() => {
          if (!isRefreshing) {
            clearInterval(interval);
            handleApi(
              endpoint,
              data,
              cancelToken,
              navigate,
              header,
              options,
              prevRoute,
            )
              .then(resolve)
              .catch(reject);
          }
        }, 50);
      }
      return;
    }
    // ─────────────────────────────────────────────────────────────────────

    let configurationObject = {
      ...endpoint,
      cancelToken: cancelToken ? cancelToken.token : null,
      data,
      withCredentials: true,
      headers: {
        "Content-Type": "application/json",
        "Web-Version": App_Version,
        ...header,
      },
      ...options,
    };

    if (endpoint.method === "get") {
      configurationObject["params"] = data;
    } else {
      configurationObject["data"] = data;
    }

    axios(configurationObject)
      .then((res) => {
        resolve(res.data);
      })
      .catch(async (err) => {
        const isNetworkError =
          err?.message === "Network Error" ||
          err?.code === "ERR_NETWORK" ||
          (!err?.response && !axios.isCancel(err));
        if (isNetworkError) {
          toast.error(
            "Unable to reach server. Check your internet connection or try again later.",
            { className: "errorToast" },
          );
          reject(err);
          return;
        }

        let responseObj;
        if (err?.response?.request?.responseType === "blob") {
          responseObj = await err.response.data.text();
          responseObj = JSON.parse(responseObj);
        }

        if (err?.response?.status === 401 || err?.response?.status === 403) {
          const detail = err?.response?.data?.detail || responseObj?.detail;
          const errData = err?.response?.data;

          if (detail === "User is inactive") {
            toast.error("Your account is inactive. Please contact support", {
              className: "errorToast",
            });
            authentication(false, navigate, true, () => (isReLogin = false));
            return;
          }

          if (errData?.is_locked) {
            toast.error(
              errData?.message ||
                "Your account has been locked. Please contact support",
              {
                className: "errorToast",
              },
            );
            authentication(false, navigate, true, () => (isReLogin = false));
            return;
          }

          if (detail === "relogin") {
            if (!isReLogin) {
              isReLogin = true;
              toast.error(
                "Session expired. Automatically logging you back in",
                { className: "errorToast" },
              );
              authentication(false, navigate, true, () => (isReLogin = false));
            }
            reject(err);
            return;
          }

          if (detail === "token_expired") {
            if (!isRefreshing) {
              isRefreshing = true;

              handleApi(REFRESH_TOKEN, null, null, navigate)
                .then(() => {
                  if (isRefreshPage) {
                    isRefreshing = false;
                    window.location.reload(); // reload only if a queued request came during refresh
                    return;
                  }

                  // Retry the original API call — handle success/failure independently
                  // so a retry failure does NOT trigger logout
                  handleApi(
                    endpoint,
                    data,
                    cancelToken,
                    navigate,
                    header,
                    options,
                  )
                    .then((retryRes) => {
                      isRefreshing = false;
                      resolve(retryRes);
                    })
                    .catch((retryErr) => {
                      isRefreshing = false;
                      reject(retryErr); // Let original caller handle this error
                    });
                })
                .catch((refreshErr) => {
                  // Only logout when the refresh token API itself fails
                  isRefreshing = false;
                  authentication(
                    false,
                    navigate,
                    true,
                    () => (isReLogin = false),
                  );
                  reject(refreshErr);
                });

              return; // Important to exit here
            } else {
              isRefreshPage = true;
              // If another refresh is ongoing, just wait and retry after
              const interval = setInterval(() => {
                if (!isRefreshing) {
                  clearInterval(interval);
                  handleApi(
                    endpoint,
                    data,
                    cancelToken,
                    navigate,
                    header,
                    options,
                  )
                    .then(resolve)
                    .catch(reject);
                }
              }, 50);

              return; // Exit here
            }
          }

          reject(err);
          return;
        }

        if (
          !_skipRNPRetry &&
          endpoint?.url.includes("rnp") &&
          err?.response?.data?.detail === "Token Expired for RNP Operations" &&
          err?.response?.status === 408 &&
          !isReLogin
        ) {
          if (!rnpVerifyPromise) {
            rnpVerifyPromise = new Promise((res) => {
              const handler = () => {
                window.removeEventListener("rnp-verified", handler);
                rnpVerifyPromise = null;
                res();
              };
              window.addEventListener("rnp-verified", handler);
            });
          }
          toast.error("Session expired. Verify your PIN to continue", {
            className: "errorToast",
          });
          window.dispatchEvent(new CustomEvent("rnp-token-expired"));
          rnpVerifyPromise.then(() => {
            handleApi(
              endpoint,
              data,
              cancelToken,
              navigate,
              header,
              options,
              prevRoute,
              _skipProactive,
              true,
            )
              .then(resolve)
              .catch(reject);
          });
          return;
        }

        reject(err);
      });
  });
};

export const WebLogin = (params) => {
  return new Promise((resolve, reject) => {
    const abort = axios.CancelToken.source();
    const id = setTimeout(() => abort.cancel("Timeout"), config.timeout);
    const getCookie = axios.create();

    let url = `${BASE_URL}/udhaar/weblogin/`;

    getCookie
      .post(url, params)
      .then((res) => {
        clearTimeout(id);
        resolve(res.data);
      })
      .catch((err) => {
        reject(err);
      });
  });
};
